package com.arena_web.ui.components.feed

import androidx.compose.runtime.Composable
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.*

/**
 * Renders post images in one of 6 layouts chosen by the author at creation:
 *   1 — single portrait    2 — single landscape
 *   3 — 2 side-by-side     4 — 2 stacked
 *   5 — 1 wide + 2 side-by-side    6 — 2 side-by-side + 1 wide
 *
 * The aspect ratios match the tile previews used in the Create Post selector
 * so what the author saw while composing is what gets rendered in the feed,
 * profile list, and comments popup.
 *
 * `singleFullWidth` makes layouts 1 and 2 (single image) take the full
 * container width instead of the 75% / 100%-but-16:9 used in the feed.
 * The comments popup uses this to fill the dialog's left column.
 */
@Composable
fun PostImageCollage(
    urls: List<String>,
    layout: Int,
    singleFullWidth: Boolean = false,
) {
    if (urls.isEmpty()) return
    val slotFillMod = Modifier.flexGrow(1).fillMaxHeight().styleModifier {
        property("flex-basis", "0")
        property("min-width", "0")
    }
    val rowFillMod = Modifier.fillMaxWidth().flexGrow(1).styleModifier {
        property("flex-basis", "0")
        property("min-height", "0")
    }
    when (layout) {
        // Layout 1 — single portrait. 3:4 aspect; full-width in the popup,
        // 75% width in the inline feed/profile list.
        1 -> if (singleFullWidth) {
            PostImageFill(
                urls[0],
                modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "3 / 4") }
            )
        } else {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                PostImageFill(
                    urls[0],
                    modifier = Modifier.fillMaxWidth(75.percent).styleModifier { property("aspect-ratio", "3 / 4") }
                )
            }
        }
        // Layout 2 — single landscape, 16:9.
        2 -> PostImageFill(
            urls[0],
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "16 / 9") }
        )
        // Layout 3 — 2 portraits side-by-side, outer 4:3.
        3 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Row(modifier = Modifier.fillMaxSize().sGap(12)) {
                PostImageFill(urls[0], modifier = slotFillMod)
                if (urls.size >= 2) PostImageFill(urls[1], modifier = slotFillMod)
            }
        }
        // Layout 4 — 2 landscape stacked, outer 4:3.
        4 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().sGap(12)) {
                PostImageFill(urls[0], modifier = rowFillMod)
                if (urls.size >= 2) PostImageFill(urls[1], modifier = rowFillMod)
            }
        }
        // Layout 5 — wide top + 2 below, outer 4:3 (rows 196:150).
        5 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().sGap(12)) {
                PostImageFill(
                    urls[0],
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "196 0 0")
                        property("min-height", "0")
                    }
                )
                if (urls.size >= 2) Row(
                    modifier = Modifier.fillMaxWidth().sGap(12).styleModifier {
                        property("flex", "150 0 0")
                        property("min-height", "0")
                    }
                ) {
                    PostImageFill(urls[1], modifier = slotFillMod)
                    if (urls.size >= 3) PostImageFill(urls[2], modifier = slotFillMod)
                }
            }
        }
        // Layout 6 — 2 top + wide below, outer 4:3 (rows 150:196).
        6 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().sGap(12)) {
                if (urls.size >= 2) Row(
                    modifier = Modifier.fillMaxWidth().sGap(12).styleModifier {
                        property("flex", "150 0 0")
                        property("min-height", "0")
                    }
                ) {
                    PostImageFill(urls[0], modifier = slotFillMod)
                    PostImageFill(urls[1], modifier = slotFillMod)
                }
                if (urls.size >= 3) PostImageFill(
                    urls[2],
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "196 0 0")
                        property("min-height", "0")
                    }
                )
            }
        }
        else -> PostImageFill(
            urls[0],
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        )
    }
}

@Composable
fun PostImageFill(url: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .sBorderRadius(12)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("background-image", "url('$url')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            }
    )
}
